// F-Empleados retrocompat · alias legacy ↔ nuevo enum EmployeeAction.
//
// Contexto: el rediseño F1 renombró las filas de EmployeeRoleActions
// (backend) de strings del ROLES_ENUM viejo a los codenames del enum
// nuevo. El front, sin embargo, sigue chequeando los strings viejos
// via `permissions.includes(ROLES_ENUM.X)` en el NavBar, TopBar,
// guards y hooks. Sin este alias el rol se ve VACÍO tras el
// backfill → menú en blanco + 401 al cambiar de empleado.
//
// Solución: para cada string legacy, listamos las acciones nuevas
// (una o varias) que hoy conceden ese permiso equivalente. Si el
// rol contiene AL MENOS UNA de ellas, expandimos el string legacy.
// Esto se aplica en `useEmployeeRole` para que el array
// `permissions` incluya nuevos + aliases legacy — y todo el
// código actual sigue funcionando sin tocarlo.
//
// Origen del mapa: exactamente el mismo que
// `LegacyActionMapper.cs` (backend). Cualquier cambio ahí debe
// replicarse aquí, o el front dejará de mostrar el menú al
// admin que edite roles con el nombre nuevo.

const LEGACY_TO_NEW = Object.freeze({
  // TPV
  TPV_MANAGER: ["ORDER_VIEW", "ORDER_CREATE", "ORDER_MODIFY_LINE", "PAYMENT_ACCEPT"],

  // Caja
  ACCESS_CAJA: ["CASH_OPEN_SESSION", "CASH_CLOSE_SESSION", "CASH_ARQUEO_VIEW"],
  ALLOW_MAKE_ARQUEO: ["CASH_ARQUEO_MODIFY"],
  MONEY_BOX_ALLOWED: ["CASH_OPEN_BOX", "MONEY_BOX_OPEN"],

  // Mesas
  ALLOW_CLOSE_TABLE: ["ORDER_CANCEL"],

  // Productos
  ALLOW_DELETE_PRODUCTS: ["ORDER_DELETE_LINE", "PRODUCT_DELETE"],

  // Cobros
  ALLOW_COBRO: ["PAYMENT_ACCEPT", "PAYMENT_SPLIT_BILL"],

  // Restaurante
  RESTAURANT_MANAGER: ["SETTINGS_MODIFY", "TABLE_LAYOUT_MANAGE"],
  RESTAURANT_TABLE_MANAGER: ["TABLE_LAYOUT_MANAGE"],
  RESTAURANT_PRINTER_MANAGER: ["PRINTERS_MANAGE"],
  RESTAURANT_BOOKING_MANAGER: [
    "BOOKING_VIEW", "BOOKING_CREATE", "BOOKING_MODIFY", "BOOKING_CANCEL",
  ],
  RESTAURANT_DISCOUNT_MANAGER: ["DISCOUNT_MANAGE", "ORDER_ADD_DISCOUNT"],
  RESTAURANT_EMPLOYEE_MANAGER: [
    "EMPLOYEE_VIEW", "EMPLOYEE_MANAGE", "EMPLOYEE_VIEW_ALERTS",
    "EMPLOYEE_APPROVE_ABSENCES", "EMPLOYEE_VIEW_SALARIES",
  ],
  // 2026-08-25 · "Sesiones QR" en Mi restaurante es la vista de QR/
  // enlaces de mesas — NO tiene nada que ver con abrir/cerrar caja.
  // Alineado con TABLE_LAYOUT_MANAGE porque los QR pertenecen a la
  // config de mesas.
  RESTAURANT_SESSIONS_MANAGER: ["TABLE_LAYOUT_MANAGE"],
  RESTAURANT_PAYMENT_METHODS_MANAGER: ["PAYMENT_METHOD_MANAGE"],

  // Inventario
  INVENTARY_MANAGER: [
    "PRODUCT_VIEW", "PRODUCT_CREATE", "PRODUCT_MODIFY", "MENU_UPDATE",
  ],
  INVENTARY_PRODUCT_MANAGER: [
    "PRODUCT_VIEW", "PRODUCT_CREATE", "PRODUCT_MODIFY",
    "PRODUCT_MODIFY_PRICE", "PRODUCT_TOGGLE_ACTIVE",
  ],
  INVENTARY_CATEGORIES_MANAGER: ["CATEGORY_MANAGE"],
  INVENTARY_TAGS_MANAGER: ["TAG_MANAGE"],
  INVENTARY_DISPLAYS_MANAGER: [
    "KDS_VIEW", "KDS_MARK_READY", "KDS_MODIFY_QUEUE", "KDS_PRINT_TICKETS",
  ],

  // Facturación
  BILLING_MANAGER: ["INVOICE_VIEW", "REPORTS_VIEW_SALES"],
  BILLING_CUSTOMERS_MANAGER: ["INVOICE_VIEW"],
  BILLING_ANALYTICS_MANAGER: [
    "REPORTS_VIEW_SALES", "REPORTS_VIEW_ANALYTICS",
    "REPORTS_VIEW_PROFIT", "REPORTS_EXPORT",
  ],
  BILLING_INVOICES_MANAGER: [
    "INVOICE_VIEW", "INVOICE_ISSUE_CUSTOM", "INVOICE_MODIFY",
  ],

  // 2026-08-25 · "Mi cuenta" — PROFILE_MANAGER legacy expande a las 3
  // acciones nuevas del grupo Account (PROFILE_MANAGE +
  // ACCOUNT_NOTIFICATIONS_MANAGE + BILLING_MANAGE). Antes PROFILE_MANAGER
  // no estaba en el mapa → se colgaba como string sin equivalente →
  // siempre falso.
  PROFILE_MANAGER: [
    "PROFILE_MANAGE",
    "ACCOUNT_NOTIFICATIONS_MANAGE",
    "BILLING_MANAGE",
  ],

  // Ajustes
  SETTINGS_MANAGER: ["SETTINGS_MODIFY"],
  SETTINGS_STRIPE_MANAGER: ["INTEGRATIONS_MANAGE"],
  SETTINGS_ACCESS: ["SETTINGS_MODIFY"],
  // 2026-08-25 · Cada sub-item de Ajustes tiene AHORA su propia acción
  // dedicada en el catálogo backend — sin efectos colaterales cruzados.
  // Antes marcar "Ver estadísticas" (REPORTS_VIEW_ANALYTICS) encendía
  // "Insights Monitor" y marcar "Ver facturas" encendía "Errores AEAT".
  SETTINGS_MONITOR_ACCESS: ["MONITOR_ACCESS"],
  SETTINGS_AUDIT_ACCESS: ["AUDIT_VIEW"],
  SETTINGS_AEAT_ACCESS: ["AEAT_ACCESS"],
  // 2026-08-25 · Escandallo/gestión con su propia acción.
  INVENTARY_GESTION_MANAGER: ["INVENTORY_MANAGE"],

  // NOTA: los strings PROFILE_*, HELP_MANAGER, BILLING_ACTIVITY_MANAGER
  // eran solo navegación y no tienen contrapartida en el enum nuevo.
  // El front los usa como toggles de visibilidad de menús secundarios;
  // los seguimos permitiendo abiertos por defecto (sin restricción).
});

/**
 * Dado un array de acciones NUEVAS (codenames del enum EmployeeAction),
 * devuelve la lista de strings LEGACY que también aplican al rol.
 * Un legacy se incluye si AL MENOS UNA de sus acciones equivalentes
 * está presente — enfoque permisivo para navegación (mostrar menú si
 * el usuario puede hacer algo dentro; el backend bloqueará acciones
 * concretas si no las tiene).
 */
export function expandLegacyAliases(newActions) {
  if (!Array.isArray(newActions) || newActions.length === 0) return [];
  const set = new Set(newActions);
  const legacy = [];
  for (const key in LEGACY_TO_NEW) {
    if (LEGACY_TO_NEW[key].some((a) => set.has(a))) legacy.push(key);
  }
  return legacy;
}

/**
 * Combinar acciones nuevas + aliases legacy. Se usa como reemplazo
 * directo de `permissions` en cualquier consumidor que chequee tanto
 * strings viejos como nuevos.
 */
export function withLegacyAliases(newActions) {
  const legacy = expandLegacyAliases(newActions);
  if (legacy.length === 0) return newActions || [];
  return [...(newActions || []), ...legacy];
}

/**
 * 2026-08-25 · Normaliza una acción declarada (legacy o nueva) al
 * conjunto de acciones NUEVAS que la representan — que es lo que
 * `useCanDo` chequea contra `info.actions[]` firmado por el backend.
 *
 * Uso: en guards del front (RequireAction, Can) que aceptan tanto
 * strings viejos (RESTAURANT_EMPLOYEE_MANAGER) como nuevos (EMPLOYEE_MANAGE).
 * Sin esto, un `<Can action="RESTAURANT_EMPLOYEE_MANAGER">` nunca
 * matchearía porque el backend jamás pone strings legacy en el JWT.
 */
export function normalizeToNewActions(input) {
  const raw = Array.isArray(input) ? input : [input];
  const out = new Set();
  for (const a of raw) {
    if (!a) continue;
    if (LEGACY_TO_NEW[a]) {
      for (const n of LEGACY_TO_NEW[a]) out.add(n);
    } else {
      out.add(a);
    }
  }
  return Array.from(out);
}

export { LEGACY_TO_NEW };
